import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import * as d3 from 'd3';
import { CommonModule } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { IInterventionDbInterface } from '../../../interfaces/intervention-db-interface';


@Component({
    selector: 'app-bar-chart',
    standalone: true,
    imports: [CommonModule, MatSelectModule],
    template: `
    <div class="mat-elevation-z2">
    <div>
      <mat-form-field appearance="outline">
        <mat-label>Select Key</mat-label>
        <mat-select [(value)]="selectedKey" (selectionChange)="updateChart()" id="select">
          <mat-option *ngFor="let key of keys" [value]="key">{{ key }}</mat-option>
        </mat-select>
      </mat-form-field>
    </div>
    <div id="barchart"></div>
    </div>
  `,
    styleUrls: ['./bar-chart.component.scss'],
})
export class BarChartComponent implements OnChanges, OnInit {
    @Input({ required: true }) data: IInterventionDbInterface[] = [];
    @Input({ required: true }) keys: string[] = [];
    @Input() height: number = 400;
    @Input() widht: number = 600;
    xLabelHeight = 10;
    margin = { top: 20, right: 30, bottom: 40, left: 40 };
    selectedKey: string | undefined;
    svg: d3.Selection<SVGGElement, unknown, HTMLElement, any>;

    graphHeight: number = this.height - this.xLabelHeight - this.margin.left - this.margin.right;
    graphWidth: number = this.widht - this.margin.top - this.margin.bottom;
    xAxis: any;
    yAxis: any;
    tooltip: any;

    ngOnInit() {
        // Tooltip
        this.tooltip = d3.select("main")
            .append("div")
            .attr("class", "d3-tooltip")
            .style("position", "absolute")
            .style("z-index", "10")
            .style("visibility", "hidden")
            .style("padding", "15px")
            .style("background", "rgba(0,0,0,0.6)")
            .style("border-radius", "5px")
            .style("color", "#fff")
            .text("a simple tooltip");


        this.svg = this.initGraph();
        this.selectedKey = this.keys[0];
        this.updateChart();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (this.svg && (changes['data'] || changes['keys'])) {
            this.updateChart();
        }
    }

    initGraph(): d3.Selection<SVGGElement, unknown, HTMLElement, any> {

        const svg = d3
            .select('#barchart')
            .append('svg')
            .attr("width", this.graphWidth + this.margin.left + this.margin.right)
            .attr("height", this.height + this.margin.top + this.margin.bottom)
            .append('g')
            .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

        return svg;
    }

    updateChart(): void {
        // Get unique values for the selected key
        const uniqueValues = Array.from(new Set(this.data.filter(d => d[this.selectedKey as keyof IInterventionDbInterface]).map(d => d[this.selectedKey as keyof IInterventionDbInterface])));
        if (!uniqueValues.length || !this.selectedKey || this.data.length === 0) {
            d3.selectAll("g > *").remove();
            this.svg
                .append("text")
                .attr("text-align", "center")
                .text("No data available for given filter");
            this._resetAxisVariables();
            return;
        }

        // Only remove message if the axis are not created
        if (!this.xAxis) {
            d3.selectAll("text").remove();
        }

        // Prepare data for the y-axis using the unique values and count
        const barplotData = uniqueValues.map(value => ({
            key: value,
            count: this._getCount(this.data, this.selectedKey as keyof IInterventionDbInterface, value)
        }));

        const xScale = d3
            .scaleBand()
            .domain(barplotData.map(d => d.key!.toString()))
            .range([0, this.graphWidth])
            .padding(0.2);

        const wrap = function (text: d3.Selection<SVGTextElement, unknown, null, undefined>, width: number): void {
            text.each(function () {
                // Use `this` to refer to the current text element in the selection
                const textElement = d3.select(this);
                const words = textElement.text().split(/\s+/).reverse();
                let word: string | undefined;
                let line: string[] = [];
                let lineNumber = 0;
                const lineHeight = 1.1; // ems
                const y = textElement.attr("y");
                let dy = 1.1; // Adjust if necessary
                let tspan = textElement.text(null)
                    .append("tspan")
                    .attr("x", 0)
                    .attr("y", y)
                    .attr("dy", dy + "em");
                let first = true;

                while ((word = words.pop())) {

                    line.push(word);
                    tspan.text(line.join(" "));
                    // Check if the current tspan length exceeds the specified width
                    if ((tspan.node()?.getComputedTextLength() || 0) > width) {
                        if (first) {
                            first = false;
                            // Handle edge case if there is only one word
                            if (words.length == 0) {
                                continue;
                            }
                        }

                        line.pop(); // Remove the last word if it exceeds width

                        // Only add text if there are still words left after pop
                        if (line.length >= 1) {
                            tspan.text(line.join(" "));
                            line = [word]; // Start a new line with the current word
                        }

                        tspan = textElement.append("tspan")
                            .attr("x", 0)
                            .attr("y", y)
                            .attr("dy", ++lineNumber * lineHeight + dy + "em")
                            .attr("dx", 0)
                            .text(word);

                    }
                }
            });
        };

        // X-axis
        if (!this.xAxis) {
            this.xAxis = this.svg.append('g')
                .attr('class', 'axis axis--x')
                .attr('transform', `translate(0,${this.graphHeight})`)
                .call(d3.axisBottom(xScale));
        } else {
            this.xAxis.call(d3.axisBottom(xScale));
        }


        // Y-axis
        const yScale = d3
            .scaleLinear()
            .domain([0, d3.max(barplotData, d => d.count)!])
            .range([this.graphHeight, 0]);


        if (!this.yAxis) {
            const yAxisTicks = yScale.ticks()
                .filter(tick => Number.isInteger(tick));
            this.yAxis = this.svg.append('g')
                .attr('class', 'axis axis--y')
                .call(d3.axisLeft(yScale).tickValues(yAxisTicks).tickFormat(d3.format("d")));
        } else {
            const yAxisTicks = yScale.ticks()
                .filter(tick => Number.isInteger(tick));
            this.yAxis.transition().duration(750).call(d3.axisLeft(yScale).tickValues(yAxisTicks).tickFormat(d3.format("d")));
        }


        const _mouseOver = (e: any, d: any) => {
            this.tooltip
                .html(`${d.key}<br/>(Total: ${d.count})`)
                .style("visibility", "visible")
                .style("max-width", "100px")
                .style("overflow-wrap", "break-word");
        }

        const _mouseLeave = (e: any, d: any) => {
            this.tooltip
                .html('')
                .style("visibility", "hidden");
        }

        const _mouseMove = (e: any, d: any) => {
            this.tooltip
                .style("left", (e.pageX + 10) + "px") // Use event pageX for positioning
                .style("top", (e.pageY - 20) + "px"); // Use event pageY for positioning
        }
        this.xAxis
            .selectAll("text")
            .attr("text-anchor", "middle")
            .call(wrap.bind(this), xScale.bandwidth() - 10);

        // Add data to graph
        const u = this.svg.selectAll<SVGRectElement, { key: string | number | Date | null; count: number; }>('rect')
            .data(barplotData);

        u.enter()
            .append('rect')
            .merge(u)
            .on('mouseover', _mouseOver)
            .on('mousemove', _mouseMove)
            .on('mouseout', _mouseLeave)
            .transition()
            .duration(1000)
            .attr('x', d => xScale(d.key!.toString())!)
            .attr('y', d => yScale(d.count))
            .attr('width', xScale.bandwidth())
            .attr('height', d => this.graphHeight - yScale(d.count))
            .attr('fill', 'var(--sys-primary)');

        u
            .exit()
            .remove()
    }


    _getCount(data: IInterventionDbInterface[], selectedKey: keyof IInterventionDbInterface, value: any): number {
        return data.filter(d => d[selectedKey] === value).length;
    }

    _resetAxisVariables() {
        this.xAxis = undefined;
        this.yAxis = undefined;
    }
}
